/**
 * The guarded front half of main.tsx's signed-in boot sequence: MSAL's
 * initialize() + handleRedirectPromise(), and the last-resort screen shown when
 * one of them rejects.
 *
 * Why it is here and not inline in main.tsx (feedback #385, "Pantalla en
 * blanco"): those two awaits run *before* createRoot is ever called, so a
 * rejection — a corrupted MSAL cache entry, a redirect-state mismatch on an
 * iPad, no network to Entra on boot — left #root empty for good, with nothing
 * in the console from app code and nothing on screen for a technician to act
 * on. ErrorBoundary cannot cover it: it only catches what is thrown during or
 * after a render that, in this failure, never happens.
 *
 * Two rules shape this module:
 *  - It is framework-agnostic and DOM-free at module scope, so the guard is
 *    unit-testable in the node test environment (entraBoot.test.ts).
 *  - The fallback is built from plain DOM nodes and inline styles, and leans on
 *    nothing but the element it is handed: no MSAL, no App, no i18n, no
 *    Tailwind. The failure it reports may be in exactly that dependency chain.
 */
import { errorMessage } from './errorUtils';

/**
 * Prefix on the one console.error this path emits. Deliberately distinct from
 * every other console.error call site ("ErrorBoundary caught:", "Error loading
 * …"), so a boot failure is greppable in a browser console and separable in
 * Application Insights.
 */
export const ENTRA_BOOT_FAILURE_TAG = '[entra-boot] sign-in setup failed before the app could render:';

export interface EntraBootSteps {
  /** msalInstance.initialize() */
  initialize(): Promise<unknown>;
  /** msalInstance.handleRedirectPromise() */
  handleRedirectPromise(): Promise<unknown>;
  /** Put something visible on screen. Called only on failure. */
  renderFallback(error: unknown): void;
}

/**
 * Runs the MSAL boot sequence. Resolves `true` when the app may mount, `false`
 * when it may not — it never rejects, because its caller is a top-level await
 * with nothing above it to catch anything.
 */
export async function runEntraBoot(steps: EntraBootSteps): Promise<boolean> {
  try {
    await steps.initialize();
    await steps.handleRedirectPromise();
    return true;
  } catch (error) {
    console.error(ENTRA_BOOT_FAILURE_TAG, error);
    try {
      steps.renderFallback(error);
    } catch (fallbackError) {
      // Still better than the blank screen: the first console.error above
      // already carries the real cause.
      console.error(`${ENTRA_BOOT_FAILURE_TAG} the fallback screen also failed:`, fallbackError);
    }
    return false;
  }
}

/** English first, Spanish under it: i18n is one of the things that may be down. */
const FALLBACK_TEXT = {
  title: 'The app could not start',
  titleEs: 'La aplicación no pudo iniciarse',
  body: 'Sign-in could not be set up on this device. Close the app completely and open it again.',
  bodyEs: 'No se pudo preparar el inicio de sesión en este dispositivo. Cierre la aplicación por completo y vuelva a abrirla.',
  help: 'If it keeps happening, tell the office and read them the message below.',
  helpEs: 'Si sigue ocurriendo, avise a la oficina y léales el mensaje de abajo.',
  retry: 'Try again · Reintentar',
};

/**
 * Draws the boot-failure screen into `root` (normally #root). Replaces whatever
 * is there, which on this path is nothing at all.
 */
export function renderBootFailureFallback(root: HTMLElement | null, error: unknown): void {
  if (!root) return;
  const doc = root.ownerDocument;
  const view = doc.defaultView;

  // The theme service never ran, so there is no data-theme-mode to read; the
  // device's own preference is the only signal available this early.
  const dark = Boolean(view?.matchMedia?.('(prefers-color-scheme: dark)')?.matches);
  const colors = dark
    ? { page: '#111827', card: '#1f2937', text: '#f3f4f6', muted: '#9ca3af', line: '#374151', code: '#111827' }
    : { page: '#f3f4f6', card: '#ffffff', text: '#111827', muted: '#4b5563', line: '#e5e7eb', code: '#f9fafb' };
  const font = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

  const el = (tag: string, style: string, text?: string) => {
    const node = doc.createElement(tag);
    node.setAttribute('style', style);
    if (text !== undefined) node.textContent = text;
    return node;
  };

  const wrap = el(
    'div',
    `min-height:100vh;display:flex;align-items:center;justify-content:center;padding:24px;` +
      `background:${colors.page};color:${colors.text};font-family:${font};`,
  );
  const card = el(
    'div',
    `max-width:28rem;width:100%;box-sizing:border-box;background:${colors.card};border:1px solid ${colors.line};` +
      `border-radius:12px;padding:24px;box-shadow:0 10px 25px rgba(0,0,0,0.12);`,
  );

  card.appendChild(el('h1', `margin:0;font-size:18px;font-weight:600;`, FALLBACK_TEXT.title));
  card.appendChild(el('p', `margin:4px 0 16px;font-size:15px;color:${colors.muted};`, FALLBACK_TEXT.titleEs));
  card.appendChild(el('p', `margin:0 0 4px;font-size:15px;line-height:1.5;`, FALLBACK_TEXT.body));
  card.appendChild(el('p', `margin:0 0 16px;font-size:15px;line-height:1.5;color:${colors.muted};`, FALLBACK_TEXT.bodyEs));
  card.appendChild(el('p', `margin:0 0 4px;font-size:14px;line-height:1.5;`, FALLBACK_TEXT.help));
  card.appendChild(el('p', `margin:0 0 16px;font-size:14px;line-height:1.5;color:${colors.muted};`, FALLBACK_TEXT.helpEs));
  card.appendChild(
    el(
      'pre',
      `margin:0 0 16px;padding:12px;background:${colors.code};border:1px solid ${colors.line};border-radius:8px;` +
        `font-size:12px;line-height:1.4;white-space:pre-wrap;word-break:break-word;color:${colors.muted};`,
      errorMessage(error),
    ),
  );

  const button = el(
    'button',
    `width:100%;padding:10px 16px;border:0;border-radius:8px;background:#2563eb;color:#ffffff;` +
      `font-size:15px;font-weight:500;font-family:inherit;cursor:pointer;`,
    FALLBACK_TEXT.retry,
  ) as HTMLButtonElement;
  button.type = 'button';
  button.addEventListener('click', () => view?.location.reload());
  card.appendChild(button);

  wrap.appendChild(card);
  root.replaceChildren(wrap);
}
